import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleOptionalService } from '../redux/slices/bookingSlice';
import RegistrationStepperHeader from '../components/RegistrationStepperHeader';
import StepperButtons from '../components/StepperButtons';

const AddonSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eventDetails, selectedService, selectedOptionalServices, totalPrice } = useSelector((state) => state.booking);

  useEffect(() => {
    if (!selectedService) {
      navigate('/service');
      return;
    }
    
    // Auto-skip if no add-ons available
    const optionalServices = eventDetails?.optionalServices || [];
    const timeOptions = eventDetails?.timeOptions || [];
    
    if (optionalServices.length === 0) {
      // No add-ons, check if time slots exist
      if (timeOptions.length === 0) {
        // No add-ons and no time slots, go directly to devotee
        navigate('/devotee');
      } else {
        // No add-ons but time slots exist, go to time-slot
        navigate('/time-slot');
      }
    }
  }, [selectedService, eventDetails, navigate]);

  const handleToggleAddon = (addon) => {
    dispatch(toggleOptionalService(addon));
  };

  const isAddonSelected = (addonId) => {
    return selectedOptionalServices.some(s => s.optional_Service_Id === addonId);
  };

  const optionalServices = eventDetails?.optionalServices || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-24">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-[28px] bg-white/70 backdrop-blur-xl shadow-[0_26px_70px_-45px_rgba(15,23,42,0.55)] border border-white/60 mb-4">
            <RegistrationStepperHeader
              title={(eventDetails?.event?.event_Name || eventDetails?.Event?.event_Name || 'Event Registration')}
              subtitle={null}
              backPath="/service"
              backLabel="Back"
              eventDetails={eventDetails}
            />

            <div className="p-6">
              <div className="rounded-2xl bg-white/85 border border-gray-200/70 shadow-sm">
                <div className="px-5 pt-4 pb-3 border-b border-gray-200/70">
                  <div className="text-sm font-semibold text-gray-900">Add-ons</div>
                  <div className="text-xs text-gray-500">Select optional services to enhance your experience (optional).</div>
                </div>

                <div className="p-5">
                  {optionalServices.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {optionalServices.map((addon) => {
                        const isSelected = isAddonSelected(addon.optional_Service_Id);
                        return (
                          <div
                            key={addon.optional_Service_Id}
                            onClick={() => handleToggleAddon(addon)}
                            className={`relative cursor-pointer rounded-2xl border transition-all overflow-hidden ${
                              isSelected
                                ? 'border-indigo-500 shadow-[0_18px_45px_-28px_rgba(79,70,229,0.55)] bg-gradient-to-b from-indigo-50/60 via-white to-white'
                                : 'border-gray-200 bg-white hover:shadow-md'
                            }`}
                          >
                            <div className="absolute inset-0 pointer-events-none">
                              <div className="absolute -left-10 -top-10 h-28 w-28 rounded-full bg-indigo-500/10 blur-2xl"></div>
                              <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-amber-400/10 blur-2xl"></div>
                            </div>

                            <div className="relative p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm border border-gray-200">
                                  <span className="text-lg">✨</span>
                                </div>

                                <div className="shrink-0 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700 border border-gray-200 shadow-sm">
                                  ${Number(addon.optional_Service_Price || 0).toFixed(2)}
                                </div>
                              </div>

                              <div className="mt-3">
                                <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-semibold text-gray-600 border border-gray-200">
                                  <i className="fas fa-sparkles text-[10px]"></i>
                                  Add-on
                                </div>
                              </div>

                              <div className="mt-3 text-sm font-semibold text-gray-900">
                                {addon.optional_Service_Name}
                              </div>

                              {addon.optional_Service_Description ? (
                                <div className="mt-1 text-xs text-gray-500 line-clamp-2">
                                  {addon.optional_Service_Description}
                                </div>
                              ) : null}

                              <div className="mt-4">
                                <div
                                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold border ${
                                    isSelected
                                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                      : 'bg-white text-gray-500 border-gray-200'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <i className="fas fa-check text-[10px]"></i>
                                      Added
                                    </>
                                  ) : (
                                    <>Tap to add</>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="relative h-1.5 bg-slate-100">
                              <div
                                className={`h-full transition-all ${
                                  isSelected
                                    ? 'w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500'
                                    : 'w-0 bg-transparent'
                                }`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                      <i className="fas fa-inbox text-gray-400 text-6xl mb-4"></i>
                      <p className="text-gray-600 text-lg mb-2">No optional services available</p>
                      <p className="text-gray-500 text-sm">Click Next to continue to time selection</p>
                    </div>
                  )}
                </div>

                {selectedOptionalServices.length > 0 && (
                  <div className="px-5 pb-5">
                    <div className="rounded-2xl bg-white/90 border border-gray-200 px-4 py-3 shadow-sm">
                      <div className="text-xs font-semibold text-gray-900 mb-2">
                        Selected Add-ons ({selectedOptionalServices.length})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedOptionalServices.map((addon) => (
                          <span
                            key={addon.optional_Service_Id}
                            className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 border border-indigo-200"
                          >
                            {addon.optional_Service_Name}
                            <span className="font-semibold text-indigo-900">
                              ${Number(addon.optional_Service_Price || 0).toFixed(2)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <StepperButtons
        wrapperClassName="bg-white/90 backdrop-blur-xl"
        containerClassName="px-4 py-3"
        backPath="/service"
        nextPath={(() => {
          const timeOptions = eventDetails?.timeOptions || [];
          return timeOptions.length > 0 ? '/time-slot' : '/devotee';
        })()}
        nextLabel="Next"
        centerContent={
          <div className="text-xs text-gray-500">
            Total:
            <span className="ml-2 text-sm font-semibold text-gray-900">
              ${Number(totalPrice || 0).toFixed(2)}
            </span>
          </div>
        }
      />
    </div>
  );
};

export default AddonSelection;
